import { Entitlement } from './entitlementApi';

// Last-known-good entitlement cache (per signed-in user).
//
// Written on every successful backend fetch; read ONLY when the fetch fails —
// i.e. when we could not get an authoritative answer (offline, timeout,
// unreachable/5xx backend). An authenticated "not pro" answer arrives as a
// successful response, so it OVERWRITES this cache rather than reading it — a
// lapsed subscription can never be resurrected by the cache once the backend
// has actually answered.
//
// TTL: 7 days. Subscriptions bill monthly/yearly and both Stripe and Apple run
// multi-day payment-retry (dunning) windows, so honoring the last verified
// state for a week stays inside the period where the user is plausibly still
// entitled — and it matches the 7-day hard expiry already used by the location
// cache (useLocation). Past the TTL a fully-offline device degrades to the
// free UI, exactly as before this cache existed.
//
// The entry is keyed by Firebase uid and only served back for that same uid,
// so a different account signing in on the same device can never inherit Pro.
// Cleared on sign-out (AuthContext.signOut) and account deletion
// (DeleteAccountFlow's localStorage.clear() plus the sign-out it triggers).

const CACHE_KEY = 'rhemn_entitlement_cache';

export const ENTITLEMENT_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface CachedEntitlementEntry {
  uid: string;
  entitlement: Entitlement;
  fetchedAt: number; // epoch ms of the successful fetch
}

export function writeCachedEntitlement(uid: string, entitlement: Entitlement): void {
  try {
    const entry: CachedEntitlementEntry = { uid, entitlement, fetchedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // storage full/unavailable — resilience feature only, never fatal
  }
}

/** Fresh last-known-good entitlement for this uid, or null. */
export function readCachedEntitlement(uid: string): Entitlement | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Partial<CachedEntitlementEntry>;
    if (
      entry?.uid !== uid ||
      typeof entry.fetchedAt !== 'number' ||
      Date.now() - entry.fetchedAt > ENTITLEMENT_CACHE_TTL_MS ||
      typeof entry.entitlement?.isPro !== 'boolean' ||
      typeof entry.entitlement?.source !== 'string' ||
      typeof entry.entitlement?.status !== 'string'
    ) {
      return null;
    }
    return entry.entitlement;
  } catch {
    return null;
  }
}

export function clearCachedEntitlement(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // non-fatal
  }
}
